/**
 * Created by Marcus Zhao on 12/01/2020
 * -------------------------------------
 */

import { Component, OnInit, Input, ViewChild, OnDestroy } from '@angular/core';
import { Subscription, Subject, ReplaySubject, of as observableOf } from 'rxjs';
import { Store } from '@ngrx/store';
import { catchError, take, debounceTime, filter } from 'rxjs/operators';
import { AppState } from '../../../../redux';
import * as _lodash from 'lodash';
import { ContactTemplateService } from '../../../contact-dialog-new/contact-template.service';
import { ControlId, CompanyAction, ContactTemplateType } from '../../../contact-dialog-new/contact-dialog.model';
import { TemplateControlValue } from '@tamalelibs/models/template-control-value.model';
import { EntityService } from '@tamalelibs/services/entity.service';
import { ContactService } from '@tamalelibs/services/contact.service';
import { OpenDynamicComponentService } from '../../../../pages/home/home-open-dynamic-component';
import { AlertWindowService } from '../../../../widgets/alert-window/alert-window.service';
import { HomeViewModel } from '../../../../pages/home/home-view.model';
import { EntityBack, EntityDialogOpenOptions, EntityDialogType } from '../../../entity-dialog/entity-dialog.model';
import { ChangeContactTemplateValueSet } from '@tamalelibs/redux/contact.actions';
import { ContactTemplateControlValue } from '@tamalelibs/models/contact-template-control-value.model';
import { Entity } from '@tamalelibs/models/entity.model';
import { businessConstants } from '../../../../constants/business.constants';

@Component({
    selector: 'tam-contact-dropdown',
    templateUrl: './contact-dropdown.component.html',
    styleUrls: ['./contact-dropdown.component.scss'],
    standalone: false
})
export class ContactDropdownComponent implements OnInit, OnDestroy {
    @Input() childConfig: any;
    @Input() valueChangeSubject: ReplaySubject<TemplateControlValue>;
    @ViewChild('dropDownEl', { static: false }) entityEl;
    config: any;
    isNotFly = true;
    readOnly = false; // flag to disable auto complete
    hasPermissionToView = true;
    selectedDictionary: any = {}; // selected items, using dictionary for quickly removing duplicates
    inputValue = ''; // input box text value
    suggestionItems: Array<any> = []; // suggestion items based on search, sub set of dataItems
    defaultItems: Array<any> = []; // defaultItems items based on search, sub set of dataItems
    selectedItem: any; // selected items, sub set of dataItems
    placeholder = '';
    hintContent = '';
    readonlyTip = '';
    showCompanyError = false;
    showEndYearError = false;
    searchId = 'searchId';
    searchItem = '';

    private _searchText = '';
    private _searchValue;
    private _filterHandler$: Subject<string> = new Subject();
    private _destroySubscriptions: Array<Subscription> = [];
    // this var is to save the original company value which has changed
    private _originalCompany = null;
    // this var is to save the original job title value which has changed
    // when create the valid company on fly, the current employment need to move to former employment
    // when create the invalid company on fly, the current employment cannot move to former employment
    private _isCreateValidEntity = true;
    private companyData: any;

    constructor(
        private _contactTemplateService: ContactTemplateService,
        private _entityService: EntityService,
        private _contactService: ContactService,
        private _store: Store<AppState>,
        private _openDynamicComponentService: OpenDynamicComponentService,
        private _alertWindow: AlertWindowService,
        private _homeViewModel: HomeViewModel,
    ) { }

    ngOnInit() {
        this._initData();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    private _initData() {
        this.config = this.childConfig.config;
        this.readOnly = this.config.readOnly || false;
        this._destroySubscriptions.push(
            this._openDynamicComponentService.feedbackSubject$.subscribe((feedback: EntityBack) => {
                if (feedback.controlId === this.config.controlID) {
                    const indexNumber = parseInt(feedback.currentId, 0);
                    let indexString = '';
                    if (indexNumber > 1) {
                        indexString = (indexNumber - 1).toString();
                    }
                    if (this.config.prefixId.toString() === indexString) {
                        this._entityService.getEntityListByIdsQuick([feedback.entityId])
                            .subscribe(res => {
                                this._setFlyValue(res, feedback);
                            });
                        this._changeValueSet();
                    }
                }
            }),
            this._contactTemplateService.validInputSubject$.subscribe((res) => {
                if (this.config.prefixId === res.prefixId && !this.selectedItem && this.config.isRequired) {
                    this.hintContent = businessConstants.tip;
                }
            }),
            this._filterHandler$.pipe(
                debounceTime(0),
            ).subscribe(filterStr => {
                if (filterStr && filterStr.length > 0) {
                    this._searching(filterStr);
                } else {
                    this._searching('');
                }
            }),
        );
        this._searching('');
        if (this.config.action === ContactTemplateType.NewContact) {
            this._initCreateValue();
            if (this.config.isSignature) {
                this._initEditValue();
            }
        } else if (this.config.action === ContactTemplateType.EditContact) {
            this._initEditValue();
        }
        this._changeValueSet();
    }

    handleFilter(value) {
        this._searchValue = value;
        this._filterHandler$.next(value);
    }

    onOpen(event: any) {
        const inputValue = this.entityEl.wrapper.nativeElement.querySelector('input', '.k-multiselect-wrap .k-floatwrap').value;
    }

    medOnFocus(event: any) {
        this._searching('');
    }

    onEnter(event) {
        event.stopImmediatePropagation();
    }

    clickContactFly() {
        this._searchValue = this.hintContent;
        this._isCreateValidEntity = false;
        this.createEntity();
    }

    valueChange(tappedItem) {
        if (tappedItem && tappedItem.id) {
            this.hintContent = '';
            this.showCompanyError = false;
            this._contactTemplateService.changeValueStatus = true;
            this._emitCompanyChangeEvent(this.selectedItem);
        }
        // up function is to adjust if need to dispatch the new added job title to store
        this._changeValueSet();

    }

    clearValue() {
        // simulate user press esc this will toggle the dropdown and remove keyword on filter
        const event = new UIEvent('kendodropdown_keydown');
        event['keyCode'] = 27;
        this.entityEl.keydown(event);
        this.inputValue = '';
        this.selectedItem = null;
        this._searching('');
        this._emitCompanyChangeEvent({ id: '', name: '' });
        this._changeValueSet();
    }

    createEntity() {
        this._showAddEntityDialog();
    }

    private _changeValueSet() {
        let value = null;
        if (this.selectedItem && this.selectedItem.id && this.selectedItem.name) {
            value = new ContactTemplateControlValue(this.selectedItem.id, this.selectedItem.name, this.config);
        } else {
            value = new ContactTemplateControlValue('', '', this.config);
        }
        this._store.dispatch(
            new ChangeContactTemplateValueSet(
                value,
                'prefix',
                this.config.prefixId
            )
        );
        this._contactTemplateService.actionSubject$.next({
            type: CompanyAction.SetCompanyValue,
            payload: {
                company: this.selectedItem
            }
        });
    }

    private _emitCompanyChangeEvent(newValue: { id: string, name: string }) {
        if (this.config.controlID === ControlId.Company) {
            this._contactTemplateService.actionSubject$.next({
                type: CompanyAction.ChangeCompany,
                payload: {
                    originalCompany: this._originalCompany,
                    targetCompany: newValue
                }
            });
            this._originalCompany = {
                id: newValue.id,
                name: newValue.name,
            };
        }
    }

    private _initEditValue() {
        if (this.config.controlID === ControlId.Company) {
            this.companyData = this.config.editValue;
            this._originalCompany = {
                id: this.companyData.id,
                name: this.companyData.name,
            };
            if (this.config.companyName === '******') {
                this.selectedItem = this.companyData;
                this.readonlyTip = 'Company field contains a private entity that you have no permission to view';
                this.readOnly = true;
                this.hasPermissionToView = false;
            } else if (this.companyData.id && this.companyData.name !== '******') {
                this._getEntityDetailWithoutAdhoc(this.companyData.id);
            } else if (!this.companyData.id && this.companyData.name) {
                this.hintContent = this.companyData.name;
                this.showCompanyError = true;
                const subscription = this._alertWindow.info('Invalid Data',
                    ['Please correct the data for below field', this.config.controlID]).subscribe((result: boolean) => {
                        subscription.unsubscribe();
                    });
            }
        }
        this._changeValueSet();
    }

    private _initCreateValue() {
        // some control need init default drop value
    }

    // this function is for create and edit
    private _getEntityDetailWithoutAdhoc(id) {
        this._entityService.getEntityDetailWithoutAdhoc(id)
            .pipe(take(1))
            .subscribe(response => {
                const entity = Entity.parse(response);
                this.selectedItem = entity;
                this._changeValueSet();
            });
    }

    private _searching(text) {
        if (this.config.controlID === 'Company') {
            if (this.entityEl && this.entityEl.loading) {
                this.entityEl.loading = true;
            }
            this._searchText = text;
            if (Array.prototype.isPrototypeOf(this.config.source) && this.config.source.length === 0) {
                return;
            } else {
                this._contactService.getContactListByText(this._searchText, null, this.config.source).pipe(
                    take(1),
                    catchError((e) => {
                        return observableOf();
                    }),
                ).subscribe(
                    res => {
                        this._searchingDone(res, text);
                        if (this.entityEl && this.entityEl.loading) {
                            this.entityEl.loading = false;
                        }
                    }
                );
            }
        }
    }

    private _searchingDone(entityList, text) {
        this.searchItem = '';
        this.suggestionItems = this._entityService.mapEntityBriefList(entityList);
        if (text === '') {
            this.defaultItems = this.suggestionItems;
        }
        const index = this.suggestionItems.findIndex((content) => content.name.toLowerCase() === text.toLowerCase() || content.shortName.toLowerCase() === text.toLowerCase());
        if (index < 0 && text) {
            this.searchItem = text;
        }
    }

    private _showAddEntityDialog() {
        let index;
        this._homeViewModel.increment();
        this._homeViewModel.getFlyIndex().pipe(take(1)).subscribe(res => index = res);
        const _entityDialogOpenOptions = new EntityDialogOpenOptions();
        _entityDialogOpenOptions.openType = EntityDialogType.NewEntity;
        // _entityDialogOpenOptions.entityName = name;
        _entityDialogOpenOptions.instanceId = index.toString();
        _entityDialogOpenOptions.searchValue = this._searchValue;
        _entityDialogOpenOptions.controlId = this.config.controlID;
        _entityDialogOpenOptions.isCreateValidEntity = this._isCreateValidEntity;
        this._openDynamicComponentService.openDialog$.next(_entityDialogOpenOptions);
    }

    private _setFlyValue(entityList, feedback: EntityBack) {
        if (entityList) {
            const item = this._entityService.mapEntityBriefList(entityList)[0];
            if (item) {
                this._originalCompany = this.selectedItem;
                this.selectedItem = item;
                this.suggestionItems = this.defaultItems;
                this.suggestionItems.unshift(this.selectedItem);
                this._changeValueSet();
                this.hintContent = '';
                this.showCompanyError = false;
                if (feedback.isCreateValidEntity) {
                    this._contactTemplateService.actionSubject$.next({
                        type: CompanyAction.CreateValidCompany,
                        payload: {
                            originalCompany: this._originalCompany,
                            targetCompany: this.selectedItem,
                        }
                    });
                } else {
                    if (this.config.controlID === 'Company') {
                        this._contactTemplateService.actionSubject$.next({
                            type: CompanyAction.CreateInvalidCompany,
                            payload: {
                                originalCompany: this._originalCompany,
                                targetCompany: this.selectedItem,
                            }
                        });
                    }
                }
                this.valueChange(this.selectedItem);
            }
        }
        this._isCreateValidEntity = true;
    }
}
